import { useState, useCallback } from 'react';
import EmergencyService from '../services/emergency.service';

/**
 * Custom hook for managing emergency appointments
 * Provides state management and API calls for emergency functionality
 */
const useEmergency = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [emergencyAppointment, setEmergencyAppointment] = useState(null);
  const [emergencyAppointments, setEmergencyAppointments] = useState([]);

  /**
   * Create a new emergency appointment
   */
  const createEmergencyAppointment = useCallback(async (appointmentData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await EmergencyService.createEmergencyAppointment(appointmentData);
      setEmergencyAppointment(response.data);
      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = err.message || 'Failed to create emergency appointment';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get emergency appointment by ID
   */
  const getEmergencyAppointment = useCallback(async (appointmentId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await EmergencyService.getEmergencyAppointment(appointmentId);
      setEmergencyAppointment(response.data);
      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch emergency appointment';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get all emergency appointments (admin/counselor only)
   */
  const getAllEmergencyAppointments = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await EmergencyService.getAllEmergencyAppointments(filters);
      setEmergencyAppointments(response.data);
      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch emergency appointments';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update emergency appointment status
   */
  const updateAppointmentStatus = useCallback(async (appointmentId, status) => {
    setLoading(true);
    setError(null);
    try {
      const response = await EmergencyService.updateAppointmentStatus(appointmentId, status);
      // Update local state
      if (emergencyAppointment && emergencyAppointment._id === appointmentId) {
        setEmergencyAppointment(response.data);
      }
      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = err.message || 'Failed to update appointment status';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [emergencyAppointment]);

  /**
   * Cancel emergency appointment
   */
  const cancelAppointment = useCallback(async (appointmentId, reason) => {
    setLoading(true);
    setError(null);
    try {
      const response = await EmergencyService.cancelAppointment(appointmentId, reason);
      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = err.message || 'Failed to cancel appointment';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Link guest appointment to user account
   */
  const linkGuestAppointment = useCallback(async (appointmentId, userId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await EmergencyService.linkGuestAppointmentToUser(appointmentId, userId);
      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = err.message || 'Failed to link appointment to user';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get crisis resources based on crisis type
   */
  const getCrisisResources = useCallback((crisisType) => {
    return EmergencyService.getCrisisResources(crisisType);
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Reset all state
   */
  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
    setEmergencyAppointment(null);
    setEmergencyAppointments([]);
  }, []);

  return {
    // State
    loading,
    error,
    emergencyAppointment,
    emergencyAppointments,

    // Methods
    createEmergencyAppointment,
    getEmergencyAppointment,
    getAllEmergencyAppointments,
    updateAppointmentStatus,
    cancelAppointment,
    linkGuestAppointment,
    getCrisisResources,
    clearError,
    reset,
  };
};

export default useEmergency;
